/**
 * videoUrl.ts — turn a pasted video link into something embeddable.
 *
 * No imports, so both the /videos page (client/src/lib/videos.ts) and the
 * admin save endpoint (api/admin/videos.ts) validate links the same way.
 */
export type VideoPlatform = "youtube" | "linkedin" | "instagram" | "tiktok";

export interface Video {
  /** Stable id, derived from the platform's own id. */
  id: string;
  /** The link as pasted: the post or video page people can open directly. */
  url: string;
  title: string;
  description: string;
  topic: string;
  /** YYYY-MM-DD */
  publishedAt: string;
  /** Who's on camera, e.g. "Anjan". */
  presenter?: string;
  /** Optional cover image link; YouTube videos get one automatically. */
  thumbnail?: string;
  /** Feature it in a Tuesday newsletter (once, newest first). For long-form videos. */
  newsletter?: boolean;
  /**
   * What's said in it is saved (data/video-transcripts/<id>.txt, never loaded
   * by the site), so the newsletter can write an issue around it.
   */
  transcript?: boolean;
}

export interface ParsedVideo {
  platform: VideoPlatform;
  /** Platform-scoped id, used for the Video id. */
  key: string;
  /** What the iframe (or <video>) loads. */
  embedUrl: string;
  /** Portrait formats (Shorts, Reels, TikTok) get a 9:16 frame. */
  vertical: boolean;
  /** Automatic cover image, when the platform offers one without an API key. */
  thumbnail?: string;
}

export const PLATFORM_LABEL: Record<VideoPlatform, string> = {
  youtube: "YouTube",
  linkedin: "LinkedIn",
  instagram: "Instagram",
  tiktok: "TikTok",
};

/**
 * Cover images LinkedIn, Instagram and TikTok hand out are signed links that
 * stop working after a few weeks, so they're never saved as a cover: the card
 * shows the branded cover instead, and the newsletter fetches a fresh one when
 * it drafts (api/cron/newsletter-draft.ts).
 */
export function isExpiringImage(url: string | undefined): boolean {
  if (!url) return false;
  let host: string;
  try { host = new URL(url).hostname; } catch { return false; }
  return /(^|\.)(licdn\.com|cdninstagram\.com|fbcdn\.net|tiktokcdn(-[a-z]+)?\.com)$/i.test(host);
}

/**
 * Read a pasted link (or an embed code's src) into an embeddable video.
 * Returns null when the link isn't one we can embed, e.g. a TikTok short link
 * (vm.tiktok.com) that only resolves by following a redirect.
 */
export function parseVideoUrl(input: string): ParsedVideo | null {
  const raw = input.trim().match(/src="([^"]+)"/)?.[1] ?? input.trim();
  let url: URL;
  try { url = new URL(raw); } catch { return null; }
  const host = url.hostname.toLowerCase().replace(/^(www|m)\./, "");
  const path = url.pathname;

  if (host === "youtu.be" || host.endsWith("youtube.com") || host === "youtube-nocookie.com") {
    const id = host === "youtu.be"
      ? path.slice(1).split("/")[0]
      : url.searchParams.get("v") ?? path.match(/^\/(?:embed|shorts|live|v)\/([\w-]{11})/)?.[1];
    if (!id || !/^[\w-]{11}$/.test(id)) return null;
    return {
      platform: "youtube",
      key: id,
      embedUrl: `https://www.youtube-nocookie.com/embed/${id}?rel=0&autoplay=1`,
      vertical: path.startsWith("/shorts/"),
      thumbnail: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
    };
  }

  if (host.endsWith("linkedin.com")) {
    // Post links carry the id as "activity-<19 digits>"; feed and embed links as a URN.
    const urn = decodeURIComponent(path).match(/urn:li:(activity|ugcPost|share):(\d+)/);
    const activity = path.match(/activity-(\d{15,})/);
    const [type, id] = urn ? [urn[1], urn[2]] : activity ? ["activity", activity[1]] : [];
    if (!id) return null;
    return {
      platform: "linkedin",
      key: id,
      embedUrl: `https://www.linkedin.com/embed/feed/update/urn:li:${type}:${id}`,
      vertical: false,
    };
  }

  if (host.endsWith("instagram.com")) {
    const m = path.match(/^\/(p|reel|reels|tv)\/([\w-]+)/);
    if (!m) return null;
    const kind = m[1] === "reels" ? "reel" : m[1];
    return {
      platform: "instagram",
      key: m[2],
      embedUrl: `https://www.instagram.com/${kind}/${m[2]}/embed`,
      vertical: kind !== "p",
    };
  }

  if (host.endsWith("tiktok.com")) {
    const id = path.match(/\/video\/(\d+)/)?.[1] ?? path.match(/\/embed(?:\/v2)?\/(\d+)/)?.[1];
    if (!id) return null;
    return { platform: "tiktok", key: id, embedUrl: `https://www.tiktok.com/embed/v2/${id}`, vertical: true };
  }

  return null;
}

/** Why a pasted link can't be used, for the admin form. */
export function videoUrlProblem(input: string): string | null {
  if (!input.trim()) return "Paste a link to the video.";
  if (parseVideoUrl(input)) return null;
  if (/vm\.tiktok\.com|vt\.tiktok\.com/.test(input)) return "Open the TikTok short link and paste the full tiktok.com/@…/video/… address.";
  if (/lnkd\.in/.test(input)) return "Open the lnkd.in link and paste the full linkedin.com post address.";
  return "Use a YouTube, LinkedIn post, Instagram or TikTok link.";
}
